import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FREQUENCY_CAPPING_ALERT_MAX_WIDTH } from './constants';

export const DEFAULT_FREQUENCY_CAPPING_SWITCH_DATA_TEST = 'frequency-capping-switch';
export const FREQUENCY_CAPPING_DISABLED_INFO_DATA_TEST = 'frequency-capping-disabled-info';

export type FrequencyCappingSwitchProps = {
  isEnabled: boolean;
  isSubmitting: boolean;
  setIsEnabled: (isEnabled: boolean) => void;
  infoAlertItems?: ReactNode[];
  dataTestId?: string;
  isDisabled?: boolean;
  disabledInfo?: ReactNode;
};

export const FrequencyCappingSwitch = ({
  isEnabled,
  isSubmitting,
  setIsEnabled,
  infoAlertItems,
  dataTestId = DEFAULT_FREQUENCY_CAPPING_SWITCH_DATA_TEST,
  isDisabled = false,
  disabledInfo,
}: FrequencyCappingSwitchProps) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="column"
      sx={{
        alignItems: 'flex-start',
      }}
    >
      <SwitchField
        checked={isEnabled}
        onChange={event => setIsEnabled(event.target.checked)}
        label={t('Enable Frequency Capping')}
        data-test={dataTestId}
        disabled={isSubmitting || isDisabled}
      />

      {isDisabled && disabledInfo && (
        <Alert
          sx={{ mt: 0.5, mb: 0.5, maxWidth: FREQUENCY_CAPPING_ALERT_MAX_WIDTH }}
          severity="info"
          data-test={FREQUENCY_CAPPING_DISABLED_INFO_DATA_TEST}
        >
          {disabledInfo}
        </Alert>
      )}

      {isEnabled && (
        <Alert sx={{ mt: 0.5, mb: 0.5, maxWidth: FREQUENCY_CAPPING_ALERT_MAX_WIDTH }} severity="info">
          {infoAlertItems && infoAlertItems.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {infoAlertItems.map((item, index) => (
                <Box component="li" key={index}>
                  {item}
                </Box>
              ))}
            </Box>
          ) : (
            <>
              {t('The Frequency Cap operates according to the')} <strong>{t('UTC')}</strong> {t('time zone.')}
            </>
          )}
        </Alert>
      )}
    </Stack>
  );
};
